import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useDefaultModel, useModels } from "@/components/chat/Composer";
import { toggleOption, toolOptions, useAvailableServers } from "@/components/chat/ToolsPicker";
import { Button, Checkbox, ErrorText, Input, Label, Modal, Select, Textarea } from "@/components/ui";
import { api } from "@/lib/api";
import { useFolders } from "@/lib/folders";
import { useChannels, type Task, type Trigger } from "@/lib/tasks";

const PRESETS: { label: string; cron: string }[] = [
  { label: "Every hour", cron: "0 * * * *" },
  { label: "Every day at 08:00", cron: "0 8 * * *" },
  { label: "Weekdays at 07:30", cron: "30 7 * * 1-5" },
  { label: "Every Monday at 09:00", cron: "0 9 * * 1" },
];

function localTz() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export default function TaskForm({ task, onClose, onSaved }: { task: Task | null; onClose: () => void; onSaved?: (t: Task) => void }) {
  const qc = useQueryClient();
  const models = useModels().data ?? [];
  const defaultModel = useDefaultModel();
  const servers = useAvailableServers().data ?? [];
  const folders = useFolders().data ?? [];
  const channels = useChannels().data ?? [];
  const t0 = task?.trigger;
  const [form, setForm] = useState({
    name: task?.name ?? "",
    action: task?.action ?? "agent",
    message: task?.message ?? "",
    conditionSql: task?.condition?.sql ?? "",
    conditionWhen: task?.condition?.when ?? "rows",
    instructions: task?.instructions ?? "",
    model_id: task?.model_id ?? defaultModel ?? "",
    folder_id: task?.folder_id ?? "",
    type: (t0?.type ?? "cron") as Trigger["type"],
    cron: t0?.type === "cron" ? t0.cron : "0 8 * * *",
    tz: (t0?.type === "cron" && t0.tz) || localTz(),
    minutes: t0?.type === "interval" ? t0.minutes : 60,
    at: t0?.type === "once" ? t0.at.slice(0, 16) : "",
    allowMachineTools: t0?.type === "webhook" ? !!t0.allow_machine_tools : false,
    servers: task?.mcp_servers ?? null,
    channels: task?.channels ?? null,
    memory_enabled: task?.memory_enabled ?? true,
    enabled: task?.enabled ?? true,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const trigger = (): Trigger => {
    switch (form.type) {
      case "cron":
        return { type: "cron", cron: form.cron, tz: form.tz };
      case "interval":
        return { type: "interval", minutes: Number(form.minutes) };
      case "once":
        return { type: "once", at: form.at, tz: localTz() };
      case "webhook":
        return { type: "webhook", allow_machine_tools: form.allowMachineTools };
      default:
        return { type: form.type } as Trigger;
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        action: form.action,
        message: form.action === "agent" ? null : form.message,
        condition: form.folder_id && form.conditionSql.trim() ? { sql: form.conditionSql, when: form.conditionWhen } : null,
        instructions: form.action === "agent" ? form.instructions : "",
        model_id: form.model_id || null,
        folder_id: form.folder_id || null,
        trigger: trigger(),
        mcp_servers: form.servers,
        channels: form.channels,
        memory_enabled: form.memory_enabled,
        enabled: form.enabled,
      };
      return task ? api.patch<Task>(`/api/tasks/${task.id}`, body) : api.post<Task>("/api/tasks", body);
    },
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      onSaved?.(t);
      onClose();
    },
  });

  const serverIds = form.servers ?? servers.filter((s) => s.default_on).map((s) => s.id);
  const channelIds = form.channels ?? ["internal", ...channels.filter((c) => c.enabled).map((c) => c.id)];
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <Modal open onClose={onClose} title={task ? "Edit task" : "New task"} wide>
      <form className="space-y-4" onSubmit={submit}>
        <Label label="Name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Check server logs" />
        </Label>
        <Label label="What it does">
          <Select value={form.action} onChange={(e) => set("action", e.target.value as typeof form.action)} className="max-w-xs">
            <option value="agent">The assistant carries out instructions</option>
            <option value="notify">Sends a reminder (no model)</option>
            <option value="checkin" disabled={!form.folder_id}>
              Starts a check-in chat in a space (no model)
            </option>
          </Select>
        </Label>
        {form.action === "agent" ? (
          <Label label="Instructions" hint="What the assistant should do each run, and when it is worth notifying you.">
            <Textarea
              rows={6}
              value={form.instructions}
              onChange={(e) => set("instructions", e.target.value)}
              required
              placeholder="Check the system logs on myserver via ssh and notify me if there is anything noteworthy (errors, failed logins, disks filling up)."
            />
          </Label>
        ) : (
          <Label label={form.action === "checkin" ? "Question" : "Reminder"} hint={form.action === "checkin" ? "The chat opens with this; you answer when you see it." : "Sent as is."}>
            <Textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} required />
          </Label>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Model">
            <Select value={form.model_id} onChange={(e) => set("model_id", e.target.value)} required={form.action === "agent"}>
              {form.action !== "agent" && <option value="">None</option>}
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.endpoint})
                </option>
              ))}
            </Select>
          </Label>
          <Label label="Space">
            <Select value={form.folder_id} onChange={(e) => set("folder_id", e.target.value)}>
              <option value="">None</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          </Label>
        </div>

        {form.folder_id && (
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <Label label="Only if (optional)" hint="A query on the space's database; :today, :now and the like work. Empty = always.">
              <Input className="font-mono" value={form.conditionSql} onChange={(e) => set("conditionSql", e.target.value)} placeholder="SELECT 1 FROM readings WHERE date(time) = :today" />
            </Label>
            <Label label="Run when it finds">
              <Select value={form.conditionWhen} onChange={(e) => set("conditionWhen", e.target.value as "rows" | "none")}>
                <option value="rows">Something</option>
                <option value="none">Nothing</option>
              </Select>
            </Label>
          </div>
        )}
        <fieldset className="space-y-3 rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
          <legend className="px-1 text-sm font-medium">When</legend>
          <Select value={form.type} onChange={(e) => set("type", e.target.value as Trigger["type"])} className="max-w-xs">
            <option value="cron">On a schedule (cron)</option>
            <option value="interval">Every N minutes</option>
            <option value="once">Once, at a time</option>
            <option value="webhook">When a webhook is called</option>
            <option value="manual">Only when I run it</option>
          </Select>
          {form.type === "cron" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {PRESETS.map((p) => (
                  <button
                    key={p.cron}
                    type="button"
                    onClick={() => set("cron", p.cron)}
                    className="rounded-md border border-zinc-200 px-2 py-0.5 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Label label="Cron expression" hint="minute hour day-of-month month day-of-week">
                  <Input className="font-mono" value={form.cron} onChange={(e) => set("cron", e.target.value)} required />
                </Label>
                <Label label="Time zone">
                  <Input value={form.tz} onChange={(e) => set("tz", e.target.value)} />
                </Label>
              </div>
            </div>
          )}
          {form.type === "interval" && (
            <Label label="Minutes between runs">
              <Input type="number" min={1} value={form.minutes} onChange={(e) => set("minutes", Number(e.target.value))} className="max-w-32" />
            </Label>
          )}
          {form.type === "once" && (
            <Label label="Run at">
              <Input type="datetime-local" value={form.at} onChange={(e) => set("at", e.target.value)} required className="max-w-60" />
            </Label>
          )}
          {form.type === "webhook" && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500">After saving, the task shows a secret URL. A POST to it starts a run; the request body is passed to the assistant.</p>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={form.allowMachineTools} onChange={(e) => set("allowMachineTools", e.target.checked)} />
                <span>
                  Allow commands and agents on workers in webhook runs
                  <span className="block text-xs text-amber-700 dark:text-amber-400">
                    Whoever calls the webhook writes part of the prompt and could try to talk the assistant into running commands. Leave off unless
                    only you can call it.
                  </span>
                </span>
              </label>
            </div>
          )}
        </fieldset>

        {servers.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-sm font-medium">Tools (MCP servers)</span>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {toolOptions(servers).map((o) => (
                <Checkbox key={o.key} label={o.label} checked={o.ids.every((id) => serverIds.includes(id))} onChange={() => set("servers", toggleOption(serverIds, o))} />
              ))}
            </div>
            <p className="text-xs text-zinc-500">Tools that need approval are not available in task runs.</p>
          </div>
        )}
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Notify via</span>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <Checkbox label="Inbox" checked={channelIds.includes("internal")} onChange={() => set("channels", toggle(channelIds, "internal"))} />
            {channels.map((c) => (
              <Checkbox key={c.id} label={c.name} checked={channelIds.includes(c.id)} onChange={() => set("channels", toggle(channelIds, c.id))} />
            ))}
          </div>
          {!channels.length && <p className="text-xs text-zinc-500">Add email, Pushover, Pushbullet or Gotify under Settings → Notifications.</p>}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox label="Task memory (notes kept between runs)" checked={form.memory_enabled} onChange={(e) => set("memory_enabled", e.target.checked)} />
          <Checkbox label="Enabled" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
        </div>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
